/**
 * LocalStorage helper for workplace request buffer settings.
 * Stores buffer times (before/after in minutes) per workplace request type.
 */

const STORAGE_KEY = "workplace_request_buffer_settings";

export interface BufferSettings {
  bufferBefore: number; // minutes before the reservation
  bufferAfter: number; // minutes after the reservation
}

export interface BufferSettingsMap {
  [typeId: string]: BufferSettings;
}

/**
 * Get all buffer settings from localStorage
 */
export function getAllBufferSettings(): BufferSettingsMap {
  if (typeof window === "undefined") return {};

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    return JSON.parse(stored) as BufferSettingsMap;
  } catch {
    return {};
  }
}

/**
 * Get buffer settings for a specific workplace request type
 */
export function getBufferSettings(typeId: string): BufferSettings | null {
  const all = getAllBufferSettings();
  return all[typeId] || null;
}

/**
 * Save buffer settings for a specific workplace request type
 */
export function saveBufferSettings(
  typeId: string,
  settings: BufferSettings
): void {
  if (typeof window === "undefined") return;

  try {
    const all = getAllBufferSettings();

    // If both values are 0, remove the entry
    if (settings.bufferBefore === 0 && settings.bufferAfter === 0) {
      delete all[typeId];
    } else {
      all[typeId] = settings;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (error) {
    console.error("Failed to save buffer settings:", error);
  }
}

/**
 * Remove buffer settings for a specific workplace request type
 */
export function removeBufferSettings(typeId: string): void {
  if (typeof window === "undefined") return;

  try {
    const all = getAllBufferSettings();
    delete all[typeId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (error) {
    console.error("Failed to remove buffer settings:", error);
  }
}

/**
 * Calculate total buffer time for given workplace request types
 * Returns the maximum buffer before and after from all selected types
 */
export function calculateTotalBuffer(typeIds: string[]): BufferSettings {
  const all = getAllBufferSettings();

  let maxBefore = 0;
  let maxAfter = 0;

  for (const typeId of typeIds) {
    const settings = all[typeId];
    if (settings) {
      maxBefore = Math.max(maxBefore, settings.bufferBefore);
      maxAfter = Math.max(maxAfter, settings.bufferAfter);
    }
  }

  return {
    bufferBefore: maxBefore,
    bufferAfter: maxAfter
  };
}
